import { useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  Input,
  Select,
  Textarea,
} from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";
import type {
  FilePurposeType,
  VerificationDecisionAction,
  VerificationType,
} from "../types";
import { formatLabel } from "../labels";

const VERIFICATION_TYPES: VerificationType[] = [
  "business_identity",
  "business_ownership",
  "payment_eligibility",
  "role_requirement",
];

const DECISIONS: VerificationDecisionAction[] = [
  "submit",
  "start_review",
  "request_more_information",
  "verify",
  "accept",
  "reject",
  "revoke",
  "supersede",
];

const FILE_PURPOSES: FilePurposeType[] = [
  "verification_document",
  "business_document",
  "business_media",
  "audit_evidence",
  "policy_attachment",
];

export interface RequestVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: { verificationType: VerificationType }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function RequestVerificationDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: RequestVerificationDialogProps) {
  const [verificationType, setVerificationType] =
    useState<VerificationType>("business_identity");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Request verification"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() => void onSubmit({ verificationType })}
          >
            Request
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Select
          label="Verification type"
          value={verificationType}
          onChange={(event) =>
            setVerificationType(event.target.value as VerificationType)
          }
          options={VERIFICATION_TYPES.map((type) => ({
            value: type,
            label: formatLabel(type),
          }))}
        />
        {error ? (
          <Alert tone="danger" title="Request failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export interface DecideVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    action: VerificationDecisionAction;
    decisionReason: string;
    approvalId?: string;
    isOverride?: boolean;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function DecideVerificationDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: DecideVerificationDialogProps) {
  const [action, setAction] =
    useState<VerificationDecisionAction>("start_review");
  const [decisionReason, setDecisionReason] = useState("");
  const [approvalId, setApprovalId] = useState("");
  const [isOverride, setIsOverride] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Verification decision"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={decisionReason.trim().length < 3}
            onClick={() =>
              void onSubmit({
                action,
                decisionReason,
                ...(approvalId.trim() ? { approvalId: approvalId.trim() } : {}),
                ...(isOverride ? { isOverride: true } : {}),
              })
            }
          >
            Submit decision
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Derived verification status on the Business is read-only. Override
          requires Dual approval when the backend enforces it.
        </Text>
        <Select
          label="Decision action"
          value={action}
          onChange={(event) =>
            setAction(event.target.value as VerificationDecisionAction)
          }
          options={DECISIONS.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Textarea
          label="Decision reason"
          value={decisionReason}
          onChange={(event) => setDecisionReason(event.target.value)}
        />
        <ApprovalPickerField
          label="Approved Approval (when required)"
          value={approvalId}
          onChange={setApprovalId}
        />
        <Checkbox
          label="Mark as verification override"
          checked={isOverride}
          onChange={(event) => setIsOverride(event.target.checked)}
        />
        {error ? (
          <Alert tone="danger" title="Decision failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export interface AttachFileDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    file_asset_id: string;
    purpose_type: FilePurposeType;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function AttachFileDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: AttachFileDialogProps) {
  const [fileAssetId, setFileAssetId] = useState("");
  const [purposeType, setPurposeType] =
    useState<FilePurposeType>("business_document");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Attach File Asset"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={fileAssetId.trim().length === 0}
            onClick={() =>
              void onSubmit({
                file_asset_id: fileAssetId.trim(),
                purpose_type: purposeType,
              })
            }
          >
            Attach
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Documents are workflow records. File Assets are stored files linked
          through File Association.
        </Text>
        <Input
          label="File Asset ID"
          value={fileAssetId}
          onChange={(event) => setFileAssetId(event.target.value)}
        />
        <Select
          label="Purpose"
          value={purposeType}
          onChange={(event) =>
            setPurposeType(event.target.value as FilePurposeType)
          }
          options={FILE_PURPOSES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? <Alert tone="danger" title="Attach failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}
